import { z } from 'zod';

import { idSchema, isoDateSchema } from './common.schema';

// ─── Flow nodes (server bot engine, GDD §2.3.2) ──────────────────────────

export const botNodeTypeSchema = z.enum([
  'message',
  'input',
  'buttons',
  'condition',
  'api_call',
  'transfer',
  'end',
]);
export type BotNodeType = z.infer<typeof botNodeTypeSchema>;

const baseNode = z.object({
  type: botNodeTypeSchema,
  next: z.string().optional(),
  on_error: z.string().optional(),
});

export const botMessageNodeSchema = baseNode.extend({
  type: z.literal('message'),
  text: z.string(),
  delay_ms: z.number().int().min(0).max(60000).optional(),
  quick_replies: z.array(z.string()).optional(),
});

export const botInputNodeSchema = baseNode.extend({
  type: z.literal('input'),
  text: z.string().optional(),
  save_as: z.string(),
  validation: z
    .object({
      type: z.enum(['email', 'phone', 'number', 'regex', 'any']).default('any'),
      pattern: z.string().optional(),
      retry_message: z.string().optional(),
    })
    .optional(),
});

export const botButtonsNodeSchema = baseNode.extend({
  type: z.literal('buttons'),
  text: z.string(),
  save_as: z.string(),
  buttons: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
        next: z.string().optional(),
      }),
    )
    .min(1)
    .max(20),
});

export const botConditionRuleSchema = z.object({
  var: z.string(),
  op: z.enum([
    'eq',
    'neq',
    'contains',
    'not_contains',
    'in',
    'gt',
    'lt',
    'regex',
    'truthy',
    'falsy',
  ]),
  val: z.unknown().optional(),
});

export const botConditionNodeSchema = baseNode.extend({
  type: z.literal('condition'),
  if: z.union([
    botConditionRuleSchema,
    z.object({
      logic: z.enum(['AND', 'OR']),
      rules: z.array(botConditionRuleSchema),
    }),
  ]),
  then: z.string(),
  else: z.string(),
});

export const botApiCallNodeSchema = baseNode.extend({
  type: z.literal('api_call'),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).default('GET'),
  url: z.string().min(1),
  headers: z.record(z.string()).optional(),
  body_template: z.string().optional(),
  save_as: z.string().optional(),
  timeout_ms: z.number().int().min(100).max(3000).optional(),
});

export const botTransferNodeSchema = baseNode.extend({
  type: z.literal('transfer'),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
  group: z.string().optional(),
  user_id: idSchema.optional(),
  context: z.array(z.string()).default([]),
  message_to_operator: z.string().optional(),
});

export const botEndNodeSchema = baseNode.extend({
  type: z.literal('end'),
  resolution_reason: z.string().optional(),
});

export const botNodeSchema = z.discriminatedUnion('type', [
  botMessageNodeSchema,
  botInputNodeSchema,
  botButtonsNodeSchema,
  botConditionNodeSchema,
  botApiCallNodeSchema,
  botTransferNodeSchema,
  botEndNodeSchema,
]);
export type BotNode = z.infer<typeof botNodeSchema>;

export const botFlowSchema = z.object({
  start: z.string().min(1),
  nodes: z.record(botNodeSchema),
});
export type BotFlow = z.infer<typeof botFlowSchema>;

export const botScenarioSchema = z.object({
  id: idSchema,
  workspace_id: idSchema,
  name: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  flow_json: botFlowSchema,
  version: z.number().int().nonnegative(),
  is_active: z.boolean(),
  source_template_id: idSchema.nullable().optional(),
  created_at: isoDateSchema,
  updated_at: isoDateSchema,
});

export const createBotScenarioSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  flow_json: botFlowSchema.optional(),
});

export const updateBotFlowSchema = z.object({
  flow_json: botFlowSchema,
});

// ─── Bot session API (виджет → сервер) ───────────────────────────────────

export const startBotSessionSchema = z.object({
  widget_id: idSchema.optional(),
  scenario_id: idSchema.optional(),
  context: z
    .object({
      page_url: z.string().nullable().optional(),
      trigger_rule: z.string().nullable().optional(),
      scroll_depth: z.number().nullable().optional(),
      time_on_page: z.number().nullable().optional(),
      utm: z.record(z.string()).optional(),
      visitor_id: z.string().optional(),
    })
    .partial()
    .optional(),
});

export const botSessionMessageSchema = z.object({
  text: z.string().max(50000).optional(),
  button_id: z.string().optional(),
  attachments: z.array(idSchema).optional(),
});

export const botSessionResponseSchema = z.object({
  session_id: idSchema,
  messages: z.array(
    z.object({
      text: z.string().optional(),
      delay_ms: z.number().int().nonnegative().optional(),
    }),
  ),
  input_type: z.enum(['text', 'buttons', 'none']).default('text'),
  choices: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
  status: z.enum(['active', 'transferred', 'ended']).default('active'),
  ticket_id: idSchema.optional(),
});
